import React from 'react';

import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import {
	ArcChart,
	Box,
	useSpring,
	useSpringRef,
	Typography,
	ArcChartRef,
	TextBlock,
	ArcChartDataItem,
	Spacer,
	Tooltip,
} from '@bcp-nextgen-dx-component-factory/design-system';
import { useMove } from '@use-gesture/react';

import { ArcChartTooltip } from '../arcChartTooltip';

interface GlobalArcChartProps {
	totalValue: number;
	data: ArcChartDataItem[];
	tooltipLabels: string[];
}

export const GlobalArcChart: React.FC<GlobalArcChartProps> = (props) => {
	const { totalValue, data, tooltipLabels } = props;
	const springRef = useSpringRef();
	const translate = useTranslation();
	const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);
	const [mouseOver, setMouseOver] = React.useState<boolean>(false);
	const [mouseOut, setMouseOut] = React.useState<boolean>(false);
	const arcChartRefBCP = React.useRef<ArcChartRef>(null);
	const spacing = 20;
	const spring = useSpring({
		from: {
			top: -200,
			left: -200,
			display: 'none',
		},
		ref: springRef,
	});

	useMove(
		({ xy: [x, y] }) => {
			if (mouseOut) {
				springRef.start({
					immediate: true,
					to: {
						display: 'none',
					},
				});
				setMouseOut(false);
				setMouseOver(false);
			} else if (mouseOver) {
				springRef.start({
					immediate: true,
					to: {
						top: Math.round(y + window.scrollY - spacing),
						left: Math.round(x),
						display: 'block',
					},
				});
			}
		},
		{
			target: document.body,
			enabled: mouseOver || mouseOut,
		}
	);

	const onMouseEnter = (index: number) => {
		setMouseOver(true);
		setSelectedIndex(index);
		arcChartRefBCP.current?.activateItems([index]);
	};

	const onMouseOut = () => {
		setMouseOut(true);
		arcChartRefBCP.current?.deactivateItems();
	};

	return (
		<Box styles={{ flexDirection: 'column', alignItems: 'center' }}>
			<ArcChartTooltip arrowSize={0} {...spring}>
				{tooltipLabels && selectedIndex !== null && (
					<Box styles={{ flexDirection: 'column', alignItems: 'center', minWidth: '128px' }}>
						<Typography colorToken="primary-dark" appearance="microcopy-75-02">
							{tooltipLabels[selectedIndex || 0]}
						</Typography>
						<TextBlock
							content={{
								floatValue: data[selectedIndex || 0].value,
								type: 'values',
								size: 'xxxSmall',
								additionalSuffix: '€',
							}}
							variant="dark"
						/>
					</Box>
				)}
			</ArcChartTooltip>
			<ArcChart
				appearance="default"
				data={data}
				description={{
					text: translate('simulator.dataDisplay.totalCost'),
				}}
				layout={{
					position: 'relative',
					width: '100%',
				}}
				mainContent={{
					currency: '€',
					value: totalValue,
				}}
				onMouseEnter={onMouseEnter}
				onMouseOut={onMouseOut}
				// onMouseEnter={function noRefCheck() {}}
				sort="descending"
			/>
			<Spacer vertical={'nano'} />
			<TextBlock.Currency
				floatValue={totalValue}
				size="medium"
				fontWeight="semiStrong"
				decimalScale={2}
				variant="light"
				currency="€"
			/>
			<Spacer vertical={'nano'} />
			<Box>
				<Typography appearance="body-100-07" colorToken="secondary-light">
					{translate('simulator.dataDisplay.totalCost')}
				</Typography>
				<Spacer horizontal={'micro'} />
				{/* <Tooltip
					inset="s5"
					content={
						<Tooltip.Container>
							<Tooltip.Description>
								<Typography appearance="body-100-07" colorToken="primary-dark">
									Inline Description
								</Typography>
							</Tooltip.Description>
						</Tooltip.Container>
					}
				>
					<Tooltip.Trigger size="xxxSmall" />
				</Tooltip> */}
			</Box>
		</Box>
	);
};
